/**
 * Goodwill Home Status: derived from all workscope goodwill statuses (where goodwill is ON).
 * Priority order (worst first): Not Started > In Progress > Unknown > Complete > No Assistance Required.
 */

export type GoodwillDisplayStatus =
  | "Not Started"
  | "In Progress"
  | "Complete"
  | "No Assistance Required"
  | "Unknown"

export type GoodwillHomeStatusResult = {
  displayLabel: GoodwillDisplayStatus
  colorClass: string
}

type GoodwillStatusPath = {
  onPath: string
  statusPath: string
  claimedByPath?: string
}

// All goodwill paths across accordions (same as ACCORDION_GOODWILL_MAP flattened).
export const GOODWILL_STATUS_PAIRS: GoodwillStatusPath[] = [
  {
    onPath: "home.treeDebrisInfo.goodwillTreeWorkOn",
    statusPath: "home.treeDebrisInfo.goodwillTreeWorkNeeded",
    claimedByPath: "home.treeDebrisInfo.goodwillTreeWorkNeededClaimedBy",
  },
  {
    onPath: "home.treeDebrisInfo.goodwillExteriorDebrisOn",
    statusPath: "home.treeDebrisInfo.goodwillExteriorDebrisRemovalNeeded",
    claimedByPath: "home.treeDebrisInfo.goodwillExteriorDebrisRemovalNeededClaimedBy",
  },
  {
    onPath: "home.treeDebrisInfo.goodwillHeavyEquipmentOn",
    statusPath: "home.treeDebrisInfo.goodwillHeavyEquipmentNeeded",
    claimedByPath: "home.treeDebrisInfo.goodwillHeavyEquipmentNeededClaimedBy",
  },
  {
    onPath: "home.roofingInfo.goodwillRoofingOn",
    statusPath: "home.roofingInfo.goodwillRoofing",
    claimedByPath: "home.roofingInfo.goodwillRoofingClaimedBy",
  },
  {
    onPath: "home.roofingInfo.goodwillRoofTarpOn",
    statusPath: "home.roofingInfo.goodwillRoofTarpNeeded",
    claimedByPath: "home.roofingInfo.goodwillRoofTarpNeededClaimedBy",
  },
  {
    onPath: "home.crawlSpaceInfo.goodwillCrawlSpaceInsulationOn",
    statusPath: "home.crawlSpaceInfo.goodwillCrawlSpaceInsulation",
    claimedByPath: "home.crawlSpaceInfo.goodwillCrawlSpaceInsulationClaimedBy",
  },
  {
    onPath: "home.crawlSpaceInfo.goodwillCrawlSpaceMuckOn",
    statusPath: "home.crawlSpaceInfo.goodwillCrawlSpaceMuck",
    claimedByPath: "home.crawlSpaceInfo.goodwillCrawlSpaceMuckClaimedBy",
  },
  {
    onPath: "home.muckGutInfo.goodwillMuckGutNeededOn",
    statusPath: "home.muckGutInfo.goodwillMuckGutNeeded",
    claimedByPath: "home.muckGutInfo.goodwillMuckGutNeededClaimedBy",
  },
  {
    onPath: "home.moldRemediationInfo.goodwillMoldRemediationOn",
    statusPath: "home.moldRemediationInfo.goodwillMoldRemediation",
    claimedByPath: "home.moldRemediationInfo.goodwillMoldRemediationClaimedBy",
  },
  {
    onPath: "home.rebuildPrepInfo.goodwillRebuildPrepOn",
    statusPath: "home.rebuildPrepInfo.goodwillRebuildPrep",
    claimedByPath: "home.rebuildPrepInfo.goodwillRebuildPrepClaimedBy",
  },
  {
    onPath: "home.roughCarpentryInfo.goodwillRoughCarpentryOn",
    statusPath: "home.roughCarpentryInfo.goodwillRoughCarpentry",
    claimedByPath: "home.roughCarpentryInfo.goodwillRoughCarpentryClaimedBy",
  },
  {
    onPath: "home.electricalInfo.goodwillElectricalOn",
    statusPath: "home.electricalInfo.goodwillElectrical",
    claimedByPath: "home.electricalInfo.goodwillElectricalClaimedBy",
  },
  {
    onPath: "home.electricalInfo.goodwillElectricalRoughInOn",
    statusPath: "home.electricalInfo.goodwillElectricalRoughIn",
    claimedByPath: "home.electricalInfo.goodwillElectricalRoughInClaimedBy",
  },
  {
    onPath: "home.hvacInfo.goodwillHvacOn",
    statusPath: "home.hvacInfo.goodwillHvac",
    claimedByPath: "home.hvacInfo.goodwillHvacClaimedBy",
  },
  {
    onPath: "home.hvacInfo.goodwillMechanicalRoughInOn",
    statusPath: "home.hvacInfo.goodwillMechanicalRoughIn",
    claimedByPath: "home.hvacInfo.goodwillMechanicalRoughInClaimedBy",
  },
  {
    onPath: "home.plumbingInfo.goodwillPlumbingOn",
    statusPath: "home.plumbingInfo.goodwillPlumbing",
    claimedByPath: "home.plumbingInfo.goodwillPlumbingClaimedBy",
  },
  {
    onPath: "home.plumbingInfo.goodwillPlumbingRoughInOn",
    statusPath: "home.plumbingInfo.goodwillPlumbingRoughIn",
    claimedByPath: "home.plumbingInfo.goodwillPlumbingRoughInClaimedBy",
  },
  {
    onPath: "home.insulationInstallInfo.goodwillInsulationInstallOn",
    statusPath: "home.insulationInstallInfo.goodwillInsulationInstall",
    claimedByPath: "home.insulationInstallInfo.goodwillInsulationInstallClaimedBy",
  },
  {
    onPath: "home.drywallHangingInfo.goodwillDrywallHangingOn",
    statusPath: "home.drywallHangingInfo.goodwillDrywallHanging",
    claimedByPath: "home.drywallHangingInfo.goodwillDrywallHangingClaimedBy",
  },
  {
    onPath: "home.mudAndTapeInfo.goodwillMudAndTapeOn",
    statusPath: "home.mudAndTapeInfo.goodwillMudAndTape",
    claimedByPath: "home.mudAndTapeInfo.goodwillMudAndTapeClaimedBy",
  },
  {
    onPath: "home.blockWallInfo.goodwillBlockWallRepairOn",
    statusPath: "home.blockWallInfo.goodwillBlockWallRepair",
    claimedByPath: "home.blockWallInfo.goodwillBlockWallRepairClaimedBy",
  },
  {
    onPath: "home.stuccoInfo.goodwillStuccoOn",
    statusPath: "home.stuccoInfo.goodwillStucco",
    claimedByPath: "home.stuccoInfo.goodwillStuccoClaimedBy",
  },
  {
    onPath: "home.bathroomRebuildInfo.goodwillBathroomRebuildOn",
    statusPath: "home.bathroomRebuildInfo.goodwillBathroomRebuild",
    claimedByPath: "home.bathroomRebuildInfo.goodwillBathroomRebuildClaimedBy",
  },
  {
    onPath: "home.cabinetInstallInfo.goodwillCabinetInstallOn",
    statusPath: "home.cabinetInstallInfo.goodwillCabinetInstall",
    claimedByPath: "home.cabinetInstallInfo.goodwillCabinetInstallClaimedBy",
  },
  {
    onPath: "home.cabinetInstallInfo.goodwillCounterInstallOn",
    statusPath: "home.cabinetInstallInfo.goodwillCounterInstall",
    claimedByPath: "home.cabinetInstallInfo.goodwillCounterInstallClaimedBy",
  },
  {
    onPath: "home.cabinetInstallInfo.goodwillApplianceInstallOn",
    statusPath: "home.cabinetInstallInfo.goodwillApplianceInstall",
    claimedByPath: "home.cabinetInstallInfo.goodwillApplianceInstallClaimedBy",
  },
  {
    onPath: "home.interiorPaintInfo.goodwillInteriorPaintOn",
    statusPath: "home.interiorPaintInfo.goodwillInteriorPaint",
    claimedByPath: "home.interiorPaintInfo.goodwillInteriorPaintClaimedBy",
  },
  {
    onPath: "home.flooringInstallInfo.goodwillFlooringInstallOn",
    statusPath: "home.flooringInstallInfo.goodwillFlooringInstall",
    claimedByPath: "home.flooringInstallInfo.goodwillFlooringInstallClaimedBy",
  },
  {
    onPath: "home.installInteriorDoorsInfo.goodwillInstallInteriorDoorsOn",
    statusPath: "home.installInteriorDoorsInfo.goodwillInstallInteriorDoors",
    claimedByPath: "home.installInteriorDoorsInfo.goodwillInstallInteriorDoorsClaimedBy",
  },
  {
    onPath: "home.trimInstallInfo.goodwillTrimInstallOn",
    statusPath: "home.trimInstallInfo.goodwillTrimInstall",
    claimedByPath: "home.trimInstallInfo.goodwillTrimInstallClaimedBy",
  },
  {
    onPath: "home.exteriorDoorInstallInfo.goodwillExteriorDoorInstallOn",
    statusPath: "home.exteriorDoorInstallInfo.goodwillExteriorDoorInstall",
    claimedByPath: "home.exteriorDoorInstallInfo.goodwillExteriorDoorInstallClaimedBy",
  },
  {
    onPath: "home.drivewayRepair.goodwillDrivewayRepairOn",
    statusPath: "home.drivewayRepair.goodwillDrivewayRepair",
    claimedByPath: "home.drivewayRepair.goodwillDrivewayRepairClaimedBy",
  },
  {
    onPath: "home.stormShutters.goodwillShutterInstallOn",
    statusPath: "home.stormShutters.goodwillShutterInstall",
    claimedByPath: "home.stormShutters.goodwillShutterInstallClaimedBy",
  },
  // Miscellaneous Goodwill: no separate "on" toggle; status field itself indicates use
  {
    onPath: "home.miscellaneousInfo.miscellaneous",
    statusPath: "home.miscellaneousInfo.miscellaneous",
    claimedByPath: "home.miscellaneousInfo.claimedBy",
  },
]

function getNestedValue(obj: unknown, path: string): unknown {
  return path.split(".").reduce((current: unknown, key) => (current as Record<string, unknown>)?.[key], obj)
}

// Priority: 1 = highest (show this), 5 = lowest. Raw status -> { priority, displayLabel, colorClass }
function mapRawToDisplay(raw: string | null | undefined): { priority: number; result: GoodwillHomeStatusResult } | null {
  if (!raw || typeof raw !== "string") return null
  const s = raw.trim()
  if (!s) return null

  // Not Started (Grey) - priority 1
  if (s === "Needed Not Started") {
    return { priority: 1, result: { displayLabel: "Not Started", colorClass: "bg-gray-100 border-gray-300 text-gray-800" } }
  }
  // In Progress (Yellow) - 75, 50, 25, Materials Only - priority 2
  if (["75%", "50%", "25%", "Materials Only"].includes(s)) {
    return { priority: 2, result: { displayLabel: "In Progress", colorClass: "bg-yellow-100 border-yellow-300 text-yellow-800" } }
  }
  // Unknown (Grey) - priority 3
  if (s === "Unknown") {
    return { priority: 3, result: { displayLabel: "Unknown", colorClass: "bg-gray-100 border-gray-300 text-gray-800" } }
  }
  // Complete (Green) - Complete, Materials Only Complete - priority 4
  if (["Complete", "Materials Only Complete"].includes(s)) {
    return { priority: 4, result: { displayLabel: "Complete", colorClass: "bg-green-100 border-green-300 text-green-800" } }
  }
  // No Assistance Required (Green) - priority 5
  if (s === "No Assistance Required") {
    return { priority: 5, result: { displayLabel: "No Assistance Required", colorClass: "bg-green-100 border-green-300 text-green-800" } }
  }
  return null
}

const DEFAULT_NO_GOODWILL: GoodwillHomeStatusResult = {
  displayLabel: "No Assistance Required",
  colorClass: "bg-green-100 border-green-300 text-green-800",
}

function claimedByIncludesHomeowner(raw: unknown): boolean {
  if (raw == null) return false
  if (Array.isArray(raw)) return raw.some(claimedByIncludesHomeowner)
  if (typeof raw !== "string") return false

  const value = raw.trim()
  if (!value) return false
  if (value === "Homeowner" || value === "Homeowner Scopes") return true

  try {
    return claimedByIncludesHomeowner(JSON.parse(value))
  } catch {
    return false
  }
}

/**
 * Compute Goodwill Home Status from form values (root object; paths are e.g. "home.treeDebrisInfo...").
 * Only considers workscopes where goodwill is ON; uses priority order (worst status wins).
 */
export function computeGoodwillHomeStatus(formValues: unknown): GoodwillHomeStatusResult {
  let best: { priority: number; result: GoodwillHomeStatusResult } | null = null

  for (const { onPath, statusPath, claimedByPath } of GOODWILL_STATUS_PAIRS) {
    const on = getNestedValue(formValues, onPath)
    if (!on) continue
    const status = getNestedValue(formValues, statusPath) as string | null | undefined
    const mapped = mapRawToDisplay(status)
    if (!mapped) continue
    if (claimedByIncludesHomeowner(claimedByPath ? getNestedValue(formValues, claimedByPath) : null)) {
      continue
    }
    if (best === null || mapped.priority < best.priority) {
      best = mapped
    }
  }

  if (best) return best.result
  return DEFAULT_NO_GOODWILL
}
